import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Loader2, Mail, Lock, Eye, EyeOff, Trophy, Shield } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { register as registerApi, login as loginApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Seo } from "@/components/Seo";

/**
 * Tabbed sign-in / sign-up page.
 *  - Sign in: email + password.
 *  - Sign up: email + password — direct, no OTP. Auto-grants 7-day Pro Max trial.
 *  - Forgot password link → /forgot (still uses OTP-by-email; only surfaced as recovery).
 */
export default function Auth() {
  const navigate = useNavigate();
  const { loginAs } = useAuth();

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/chat";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const validEmail = email.includes("@") && email.includes(".");
  const passwordIssue = (() => {
    if (!password) return "Choose a password";
    if (password.length < 8) return "At least 8 characters";
    return null;
  })();

  const handleSignin = async (e) => {
    e.preventDefault();
    if (!validEmail) return toast.error("Enter a valid email");
    if (!password) return toast.error("Enter your password");
    setLoading(true);
    try {
      const res = await loginApi({ email, password });
      loginAs(res.data.access_token, res.data.user);
      toast.success("Welcome back");
      navigate("/chat");
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not sign in";
      // 409 = legacy passwordless account, prompt to use forgot-password
      if (err.response?.status === 409) {
        toast.message(detail, {
          action: {
            label: "Set password",
            onClick: () => navigate(`/forgot?email=${encodeURIComponent(email)}`),
          },
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validEmail) return toast.error("Enter a valid email");
    if (passwordIssue) return toast.error(passwordIssue);
    setLoading(true);
    try {
      const res = await registerApi({ email, password });
      loginAs(res.data.access_token, res.data.user);
      toast.success("Welcome to Sabzy — your 7-day Pro Max trial is active");
      navigate("/chat");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        toast.message(detail || "Already registered — try signing in", {
          action: {
            label: "Sign in",
            onClick: () => setMode("signin"),
          },
        });
      } else {
        toast.error(detail || "Could not create your account");
      }
    } finally {
      setLoading(false);
    }
  };

  const isSignin = mode === "signin";

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <Seo
        title={isSignin ? "Sign in to Sabzy AI" : "Create your Sabzy AI account"}
        description="Email + password sign-in. Sign up in 60 seconds and unlock a 7-day Pro Max trial — no credit card needed."
        path="/auth"
        noindex
      />

      <div className="absolute inset-0 -z-10 grain opacity-60" />
      <div className="absolute inset-0 -z-10 grid-overlay opacity-30" />
      <div className="absolute -left-32 top-1/3 -z-10 h-[520px] w-[520px] rounded-full bg-primary/20 blur-[140px]" />
      <div className="absolute -right-24 bottom-0 -z-10 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[140px]" />

      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 sm:px-10">
          <Link to="/" data-testid="auth-logo"><Logo /></Link>
          <div className="flex items-center gap-2">
            <Link to="/help" className="hidden text-xs text-muted-foreground transition hover:text-foreground sm:inline" data-testid="auth-nav-help">
              Need help?
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto grid min-h-screen w-full max-w-7xl items-center gap-10 px-6 pt-24 pb-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pt-32">
        <section className="hidden flex-col gap-8 lg:col-span-6 lg:flex">
          <div className="label-mono text-primary">[ Enter the terminal ]</div>
          <h1 className="text-balance text-5xl font-light leading-[0.98] tracking-[-0.035em] xl:text-6xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Your email.<br />
            <span className="copper-text font-medium">Your password.</span><br />
            All of Sabzy.
          </h1>
          <p className="max-w-md text-[15px] leading-relaxed text-muted-foreground">
            Email + password. Instant access — no verification step. 7-day Pro Max trial included automatically.
          </p>
          <div className="mt-2 flex items-center gap-4 border-t border-border/40 pt-6 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              all systems nominal
            </span>
            <span>·</span><span>200 ms avg p95</span><span>·</span><span>SOC-friendly</span>
          </div>
        </section>

        <section className="lg:col-span-6">
          <div className="relative mx-auto w-full max-w-md">
            <div className="absolute -inset-2 -z-10 rounded-[28px] bg-primary/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-[24px] border border-border/60 bg-card/70 p-8 backdrop-blur-xl shadow-[0_30px_120px_-20px_rgba(0,0,0,0.6)] sm:p-10 animate-fade-up" data-testid="auth-card">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

              <div className="mb-6 flex items-center"><Logo size={26} /></div>

              {/* Tab switcher */}
              <div className="mb-6 inline-flex rounded-full border border-border/60 bg-background/40 p-1 text-xs font-semibold" data-testid="auth-mode-toggle">
                <button
                  type="button"
                  className={`rounded-full px-4 py-1.5 transition ${isSignin ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground"}`}
                  onClick={() => setMode("signin")}
                  data-testid="auth-tab-signin"
                >
                  Sign in
                </button>
                <button
                  type="button"
                  className={`rounded-full px-4 py-1.5 transition ${!isSignin ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground"}`}
                  onClick={() => setMode("signup")}
                  data-testid="auth-tab-signup"
                >
                  Sign up
                </button>
              </div>

              <h2 className="text-3xl font-semibold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.025em" }}>
                {isSignin ? "Welcome back" : "Create your account"}
              </h2>

              <Button
                type="button"
                variant="outline"
                onClick={handleGoogle}
                className="mt-6 h-12 w-full rounded-2xl border-border/70 bg-background/40 text-[14px] font-medium hover:bg-background/70"
                data-testid="auth-google-btn"
              >
                <svg className="mr-2.5 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Continue with Google
              </Button>
              <div className="mt-5 flex items-center gap-3 text-[11px] uppercase tracking-widest text-muted-foreground/70">
                <span className="h-px flex-1 bg-border/60" /> or with email <span className="h-px flex-1 bg-border/60" />
              </div>

              <form onSubmit={isSignin ? handleSignin : handleSignup} className="mt-5 space-y-4" data-testid="auth-form">
                {/* Email */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex w-12 items-center justify-center text-muted-foreground">
                    <Mail className="h-4 w-4" />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@domain.com"
                    required
                    autoComplete="email"
                    autoFocus
                    className="h-14 rounded-2xl border-border/70 bg-background/60 pl-12 pr-4 text-[15px] focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30"
                    data-testid="auth-email-input"
                  />
                </div>

                {/* Password — shown for both sign-in and sign-up */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex w-12 items-center justify-center text-muted-foreground">
                    <Lock className="h-4 w-4" />
                  </div>
                  <Input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isSignin ? "Your password" : "Choose a strong password"}
                    required
                    minLength={isSignin ? undefined : 8}
                    autoComplete={isSignin ? "current-password" : "new-password"}
                    className="h-14 rounded-2xl border-border/70 bg-background/60 pl-12 pr-12 text-[15px] focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30"
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-muted-foreground hover:text-foreground"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    data-testid="auth-toggle-password-visibility"
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {!isSignin && password && passwordIssue && (
                  <div className="-mt-1 px-1 text-[11px] text-muted-foreground" data-testid="auth-password-hint">
                    {passwordIssue}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="group h-14 w-full rounded-2xl text-[15px] font-medium shadow-lg shadow-primary/20"
                  data-testid="auth-submit-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <>
                      {isSignin ? "Sign in" : "Create account · Start trial"}
                      <ArrowRight className="ml-2 h-4 w-4 transition group-hover:translate-x-0.5" />
                    </>
                  )}
                </Button>

                {isSignin && (
                  <div className="flex justify-end">
                    <Link to={`/forgot${email ? `?email=${encodeURIComponent(email)}` : ""}`} className="text-xs text-muted-foreground transition hover:text-primary" data-testid="auth-forgot-link">
                      Forgot password?
                    </Link>
                  </div>
                )}
              </form>

              {/* Trial banner (sign-up only) */}
              {!isSignin && (
                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-3.5" data-testid="auth-trial-banner">
                  <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary">
                    <Trophy className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[13px] font-medium text-foreground">7 days of Pro Max — free</div>
                    <div className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                      ChatGPT Astra + Claude Opus 5 + Gemini Pro + live web search. No card. Downgrades automatically.
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                <Shield className="h-3 w-3" />
                Encrypted in transit · we never sell your data
              </div>
            </div>

            <p className="mt-5 text-center text-[11px] leading-relaxed text-muted-foreground">
              By continuing you agree to Sabzy&apos;s{" "}
              <Link to="/privacy" className="text-foreground/80 underline-offset-2 hover:underline" data-testid="auth-link-privacy">Privacy Policy</Link>
              .{" "}
              <Link to="/" className="text-foreground/80 underline-offset-2 hover:underline" data-testid="auth-back-home">Back to home</Link>
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
